<template>
    <div class="flex flex-col h-full w-full gap-2 p-2 overflow-y-auto">
        <div class="flex flex-wrap items-center justify-between">
            <transaction-filter
                enable-date-filter
                enable-search-filter
                enable-deleted-transaction-filter
                @update="onUpdateFilter"
            />
            <UButton
                size="sm"
                variant="outline"
                color="neutral"
                icon="i-ph-download"
                :loading="exporting"
                @click="exportCsv"
            >
                Eksporter CSV
            </UButton>
        </div>
        <div
            data-cy="transactions"
            class="pr-1 flex flex-col gap-1 grow"
        >
            <loading-bar :style="{visibility: loading ? 'visible' : 'hidden'}" data-cy="loading-indicator"/>
            <transaction-list :transactions="transactions" :loading="loading"/>
        </div>
        <pager
            :page-number="searchParams.page"
            :total-pages="totalPages"
            @page-changed="setPage"
        />
    </div>
</template>

<script setup lang="ts">
import {onMounted, onUnmounted, provide, ref} from 'vue'
import TransactionList from '@/transaction-journal/TransactionList.vue'
import {hasSelectedOrg, orgSelectionComplete} from '@/tenants/org.service'
import TransactionFilter from '@/transaction/TransactionFilter.vue'
import {defaultSearchParams, TransactionSearchParams} from '@/types/transaction/TransactionSearchParams'
import Pager from '@/components/Pager.vue'
import {Transaction} from '@/types/transaction/Transaction'
import LoadingBar from '@/components/LoadingBar.vue'
import {bindEventHandlers, loading, onCleanup, searchParams, setPage, totalPages, transactions} from '@/transaction/transaction.service'
import {useUserPrefs} from '@/user-prefs/userPrefs.service'
import {createTransactionJournalCSVExport} from '@/services/reports/summaryReport.service'
import {download} from '@/services/infrastructure/storage.service'

const {hideDeletedTransactions} = useUserPrefs()
const editorPanel = ref(null)
const exporting = ref(false)

const exportCsv = async () => {
    exporting.value = true
    try {
        const reportMeta = await createTransactionJournalCSVExport(searchParams.value)
        await download(reportMeta)
    } finally {
        exporting.value = false
    }
}

const onUpdateFilter = (newParams: TransactionSearchParams) => {
    searchParams.value = newParams
    searchParams.value.query = newParams.query ? newParams.query : undefined
    setPage(0)
}

onMounted(async () => {
    await orgSelectionComplete
    if (!hasSelectedOrg()) {
        console.error('Accessing the accounting page without selected org. Should not be possible, this is a bug!')
    }
    const defaultSearch = defaultSearchParams(false)
    if (!hideDeletedTransactions.value) {
        searchParams.value.isDeleted = false
    }
    searchParams.value.fromDate = defaultSearch.fromDate
    searchParams.value.toDate = defaultSearch.toDate
    searchParams.value.query = defaultSearch.query
    bindEventHandlers()
})

provide('editClone', (trans: Transaction) => {
    (editorPanel.value as any).cloneFromExisting(trans)
})
provide('editExisting', (trans: Transaction) => {
    (editorPanel.value as any).editExisting(trans)
})

onUnmounted(onCleanup)


</script>

<style scoped>
@reference "@/index.css";
@reference "tailwindcss";

@media (max-width: 800px) {
    .show-hidden {
        order: 2;
    }
}


</style>